import { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { APP_NAME } from "@/lib/branding";
import { BrandLogo } from "@/components/ui/brand-logo";
import { Menu, LogOut, Settings, User, ChevronDown, Lightbulb, Search, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { UserAvatar } from "@/components/ui/user-avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AppSidebar, SearchTrigger } from "./app-sidebar";
import { SidebarProvider, useSidebar } from "./sidebar-context";
import { BreadcrumbTrail } from "./breadcrumb-trail";
import {
  CommandPaletteProvider,
  useCommandPalette,
} from "@/components/search/command-palette-provider";
import { ImpersonationBanner } from "./impersonation-banner";
import { ImpersonationPicker } from "./impersonation-picker";
import { ReportProblemTrigger } from "@/components/support/report-problem-trigger";
import { openReportDialog } from "@/lib/error-report";
import { NotificationBell } from "./notification-bell";
import { ClientNavbar } from "./client-navbar";
import { PendingTrainingBanner } from "@/components/training/pending-training-banner";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";

/**
 * Páginas que são uma área de trabalho e não uma folha de leitura: ocupam o painel todo, sem margens nem rolagem da moldura
 * (a página gere o seu próprio scroll por dentro). O calendário é uma grelha — com o respiro à volta perdia um terço do ecrã.
 */
const FULL_BLEED_PATHS = ["/dashboard/calendar", "/dashboard/hr/calendar"];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout = ({ children }: DashboardLayoutProps) => (
  <CommandPaletteProvider>
    <SidebarProvider>
      <DashboardShell>{children}</DashboardShell>
    </SidebarProvider>
  </CommandPaletteProvider>
);

/**
 * A moldura da aplicação.
 *
 * Três camadas, de fora para dentro: o fundo neutro (`bg-muted`), o menu lateral
 * assente nele sem borda nenhuma, e o conteúdo num painel branco arredondado que
 * flutua sobre o fundo. É o degrau de cor — não uma linha — que separa o menu do
 * trabalho, e é o que dá à app a leveza do ecrã de entrada.
 */
const DashboardShell = ({ children }: DashboardLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { collapsed } = useSidebar();
  const { open: openCommandPalette } = useCommandPalette();

  const isClientPage =
    location.pathname.startsWith("/dashboard/clients/") &&
    location.pathname.split("/").length > 3;

  const isFullBleed = FULL_BLEED_PATHS.includes(location.pathname.replace(/\/$/, ""));

  // Get first and last name only
  const displayName = useMemo(() => {
    if (!user?.name) return "Utilizador";
    const parts = user.name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }, [user?.name]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    // `viewport-fit=cover` (no index.html) estende a app por baixo da status bar
    // e do notch — é o que a faz parecer nativa. Mas quem instala a PWA fica com
    // o cabeçalho (e o botão de menu!) escondido debaixo do relógio, sem forma de
    // navegar. O `env(safe-area-inset-*)` devolve esse espaço. No browser os
    // insets são 0, por isso isto não muda nada fora do modo instalado.
    <div
      className="flex h-dvh flex-col overflow-hidden bg-muted"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* A barra de impersonação fica ACIMA de tudo (menu incluído) — encaixada
          ao lado do menu passava despercebida. */}
      <ImpersonationBanner />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Menu lateral (desktop) — sem borda: o fundo já o separa. */}
        <aside
          className={cn(
            "hidden shrink-0 transition-[width] duration-200 ease-out lg:block",
            collapsed ? "w-[60px]" : "w-[248px]"
          )}
        >
          <AppSidebar />
        </aside>

        {/* Menu lateral (telemóvel) — abre sempre inteiro, nunca em modo ícone.
            A gaveta é `fixed` e escapa ao padding do contentor: precisa do seu
            próprio safe-area, senão o topo do menu nasce debaixo do relógio. */}
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent
            side="left"
            className="w-72 max-w-[85vw] border-r-0 bg-muted p-0"
            style={{
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <AppSidebar forceExpanded onItemClick={() => setSidebarOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* O painel do conteúdo. `pl-0` no desktop porque o menu já dá o respiro
            à esquerda; no telemóvel encosta a todas as margens. */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden p-0 lg:py-2 lg:pr-2">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background lg:rounded-xl lg:border lg:border-border/60 lg:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.10)]">
            {/* Cabeçalho */}
            <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border/60 px-3 sm:px-4">
              {/* Telemóvel: abrir menu + marca (no desktop a marca está no menu) */}
              <div className="flex shrink-0 items-center gap-1.5 lg:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(true)}
                  aria-label="Abrir menu"
                  className="h-9 w-9 shrink-0"
                >
                  <Menu className="h-5 w-5" strokeWidth={1.75} />
                </Button>
                <Link to="/dashboard" aria-label={APP_NAME}>
                  <BrandLogo className="h-7 w-auto object-contain" fallbackClassName="text-base" />
                </Link>
              </div>

              {/* Onde estamos */}
              <div className="hidden min-w-0 flex-1 lg:flex">
                <BreadcrumbTrail />
              </div>

              {/* Pesquisa — ao centro, o sítio onde o olho a procura.
                  Em telemóvel é só o ícone: instalada como PWA não há barra de
                  endereço, e sem pesquisa a única forma de chegar a algo era
                  abrir o menu e caçar. */}
              <div className="ml-auto hidden w-full max-w-xs lg:block">
                <SearchTrigger onClick={() => openCommandPalette()} />
              </div>

              {/* Acções */}
              <div className="ml-auto flex items-center gap-0.5 lg:ml-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openCommandPalette()}
                  aria-label="Pesquisar"
                  className="h-9 w-9 shrink-0 lg:hidden"
                >
                  <Search className="h-5 w-5" strokeWidth={1.75} />
                </Button>

                {/* Ver a app como outro colaborador (só superadmin) */}
                <ImpersonationPicker />

                <ReportProblemTrigger />

                <NotificationBell />

                <div className="mx-1.5 hidden h-5 w-px bg-border sm:block" />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-9 gap-2 rounded-lg pl-1.5 pr-1 hover:bg-foreground/4"
                    >
                      <UserAvatar
                        profileImageUrl={user?.profile_image_url}
                        name={user?.name || "Utilizador"}
                        size="sm"
                        className="h-7 w-7 text-xs"
                      />
                      <span className="hidden max-w-[120px] truncate text-[13px] font-medium lg:inline-block">
                        {displayName}
                      </span>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-60 overflow-hidden rounded-xl border-border/60 p-1.5"
                    sideOffset={10}
                  >
                    {/* Quem sou eu — com cara, não só nome. */}
                    <DropdownMenuLabel className="flex items-center gap-3 px-2 py-2.5 font-normal">
                      <UserAvatar
                        profileImageUrl={user?.profile_image_url}
                        name={user?.name || "Utilizador"}
                        size="sm"
                        className="h-9 w-9 text-xs"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-[13px] font-medium leading-tight">
                          {displayName}
                        </p>
                        <p className="mt-0.5 truncate text-[11px] leading-tight text-muted-foreground">
                          {user?.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator className="-mx-1.5 bg-border/60" />

                    <DropdownMenuGroup className="py-1">
                      <DropdownMenuItem asChild className="rounded-lg">
                        <Link to="/dashboard/settings/account" className="cursor-pointer">
                          <User className="mr-2.5 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                          A minha conta
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="rounded-lg">
                        <Link to="/dashboard/settings" className="cursor-pointer">
                          <Settings className="mr-2.5 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                          Definições
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="rounded-lg">
                        <Link to="/dashboard/help" className="cursor-pointer">
                          <BookOpen className="mr-2.5 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                          Manual
                        </Link>
                      </DropdownMenuItem>
                      {/* O mesmo diálogo da bandeira: um problema ou uma sugestão são, no fundo, um pedido à equipa. */}
                      <DropdownMenuItem
                        className="cursor-pointer rounded-lg"
                        onClick={() => openReportDialog()}
                      >
                        <Lightbulb className="mr-2.5 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                        Reportar ou sugerir
                      </DropdownMenuItem>
                    </DropdownMenuGroup>

                    <DropdownMenuSeparator className="-mx-1.5 bg-border/60" />

                    <DropdownMenuItem
                      variant="destructive"
                      onClick={handleLogout}
                      className="cursor-pointer rounded-lg"
                    >
                      <LogOut className="mr-2.5 h-4 w-4" strokeWidth={1.75} />
                      Terminar sessão
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </header>

            <PendingTrainingBanner />

            {/* Navegação do cliente (nas páginas de cliente) */}
            {isClientPage && (
              <div className="shrink-0 border-b border-border/60">
                <ClientNavbar />
              </div>
            )}

            {/* Conteúdo. Os lados são os do cabeçalho (`px-3 sm:px-4`): o título da página alinha com o caminho de navegação. */}
            <main
              className={cn(
                "min-h-0 flex-1 overflow-x-hidden overscroll-contain [container-type:size]",
                isFullBleed ? "flex flex-col overflow-hidden" : "overflow-y-auto"
              )}
            >
              {isFullBleed ? (
                <div className="flex min-h-0 w-full flex-1 flex-col">{children}</div>
              ) : (
                <div className="mx-auto w-full px-(--content-px) py-(--content-py)">{children}</div>
              )}
            </main>
          </div>
        </div>
      </div>

    </div>
  );
};
